/**
 * Graph Explorer - Core Module
 * Main controller for the graph explorer component
 */

(function(global) {
    'use strict';

    // ============================================
    // Explorer State
    // ============================================
    const state = {
        theme: 'dark',
        nodeLabelsVisible: true,
        edgeLabelsVisible: false,
        physicsEnabled: true,
        selectionMode: 'single',
        currentColorAttribute: 'resource_name',
        currentSizeScale: 2,
        currentSolver: 'forceAtlas2Based',
        currentLayout: 'default',
        hierarchicalSortMethod: 'hubsize',
        hierarchicalLevelSeparation: 150,
        nodeColorMap: {},
        originalNodeSizes: {},
        originalNodeColors: {},  // Store original node colors for restore
        originalEdgeColors: {},  // Store original edge colors for restore
        hiddenFilters: [],
        apiEndpoint: null,
        extensionMode: false,
        selectedNodes: [],
        selectedEdges: [],
        selectedAlias: null  // Track which alias the selection comes from
    };

    // ============================================
    // Initialization
    // ============================================
    function init(options = {}) {
        state.network = options.network || null;
        state.nodes = options.nodes || null;
        state.edges = options.edges || null;
        state.apiEndpoint = options.config?.apiEndpoint || options.apiEndpoint || null;
        state.extensionMode = options.config?.extensionMode || options.extensionMode || false;
        state.theme = options.config?.theme || options.theme || 'dark';
        state.physicsEnabled = options.config?.physicsEnabled !== false;

        // Apply initial theme
        if (state.theme === 'light') {
            document.querySelector('.graph-explorer')?.classList.add('light-theme');
        }

        // Apply extension mode
        if (state.extensionMode) {
            document.querySelector('.graph-explorer')?.classList.add('extension-mode');
        }

        // Setup responsive behavior
        setupResponsive();
        
        // Store original edge colors at initialization
        storeOriginalEdgeColors();
        
        // Setup message listener for external selection commands
        setupMessageListener();
        
        // Note: multiselect is enabled by default in GraphExplorerMinimal
        // Ctrl+Click allows multi-selection via vis.js built-in behavior

        console.log('[GraphExplorer] Core initialized', { 
            theme: state.theme, 
            physics: state.physicsEnabled,
            extension: state.extensionMode 
        });
        
        // Apply default color mapping by resource_name (so colors are correct on first load,
        // not just after the first refreshGraph call)
        if (state.nodes && state.nodes.length > 0) {
            setColorByAttribute(state.currentColorAttribute || 'resource_name');
        }

        // Build initial legend from existing node colors
        buildInitialLegend();
        
        // Check if graph is empty and show empty state
        checkEmptyState();
        
        // Listen for node changes to update empty state
        if (state.nodes) {
            state.nodes.on('*', function() {
                checkEmptyState();
            });
        }
    }
    
    // ============================================
    // Empty State Management
    // ============================================
    function checkEmptyState() {
        var overlay = document.getElementById('empty-state-overlay');
        var loadingBar = document.getElementById('loadingBar');
        
        var nodeCount = state.nodes ? state.nodes.length : 0;
        
        if (nodeCount === 0) {
            // Show empty state, hide loading bar
            if (overlay) {
                overlay.style.display = 'flex';
                // Re-initialize lucide icons for the overlay
                if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                }
            }
            if (loadingBar) {
                loadingBar.classList.add('hidden');
            }
        } else {
            // Hide empty state
            if (overlay) {
                overlay.style.display = 'none';
            }
        }
    }
    
    function showEmptyState() {
        var overlay = document.getElementById('empty-state-overlay');
        var loadingBar = document.getElementById('loadingBar');
        if (overlay) {
            overlay.style.display = 'flex';
            if (typeof lucide !== 'undefined') {
                lucide.createIcons();
            }
        }
        if (loadingBar) {
            loadingBar.classList.add('hidden');
        }
    }
    
    function hideEmptyState() {
        var overlay = document.getElementById('empty-state-overlay');
        if (overlay) {
            overlay.style.display = 'none';
        }
    }
    
    // ============================================
    // Build Initial Legend from Current Nodes
    // ============================================
    function buildInitialLegend() {
        if (!state.nodes) return;
        
        var allNodes = state.nodes.get();
        var colorToAttribute = {};
        var attribute = state.currentColorAttribute || 'domain';
        
        // Group nodes by attribute value and collect unique colors
        allNodes.forEach(function(node) {
            var attrValue = attribute === 'resource_name'
                ? getResourceNameLabel(node)
                : (node[attribute] || (node.resource_name ? node.resource_name : 'Unknown'));
            var color = node.color;
            
            // Normalize color
            if (typeof color === 'object') {
                color = color.background || color.color || '#aaaaaa';
            }
            
            if (!colorToAttribute[attrValue]) {
                colorToAttribute[attrValue] = color;
            }
        });
        
        state.legendColorMap = colorToAttribute;
        console.log('[GraphExplorer] Built initial legend with', Object.keys(colorToAttribute).length, 'categories');
    }
    
    // ============================================
    // Store Original Colors
    // ============================================
    function storeOriginalEdgeColors() {
        if (!state.edges) return;
        
        var allEdges = state.edges.get();
        allEdges.forEach(function(edge) {
            var origColor = edge.color;
            var storedColor;
            
            // Normalize color to always store the actual color string
            if (typeof origColor === 'string') {
                storedColor = origColor;
            } else if (origColor && typeof origColor === 'object') {
                storedColor = origColor.color || '#7f8c8d';
            } else {
                storedColor = '#7f8c8d';
            }
            
            state.originalEdgeColors[edge.id] = {
                color: storedColor,
                width: edge.width || 2,
                dashes: edge.dashes
            };
        });
        
        console.log('[GraphExplorer] Stored original colors for', allEdges.length, 'edges');
    }
    
    // ============================================
    // Selection Highlighting with Opacity
    // ============================================
    
    /**
     * Dim non-selected nodes and edges for visual contrast (SINGLE NODE SELECTION mode)
     * Shows: selected node + ALL connected edges + neighbor nodes
     * Optimized: tracks dimmed state and only updates changed elements.
     */
    function dimNonSelectedNodes(selectedNodeIds) {
        if (!state.nodes || !state.edges) return;
        
        var allEdges = state.edges.get();
        var selectedSet = new Set(selectedNodeIds);
        
        // Find all nodes connected to selected nodes (neighbors)
        var connectedNodeIds = new Set();
        var connectedEdgeIds = new Set();
        
        allEdges.forEach(function(edge) {
            if (selectedSet.has(edge.from)) {
                connectedNodeIds.add(edge.to);
                connectedEdgeIds.add(edge.id);
            }
            if (selectedSet.has(edge.to)) {
                connectedNodeIds.add(edge.from);
                connectedEdgeIds.add(edge.id);
            }
        });
        
        // Previous dimmed state for delta detection
        var prevNodeState = state._dimmedNodeState || {};
        var prevEdgeState = state._dimmedEdgeState || {};
        var newNodeState = {};
        var newEdgeState = {};
        var wasDimmed = Object.keys(prevNodeState).length > 0;
        
        var fontColor = state.theme === 'light' ? '#212529' : '#ffffff';
        var dimmedFontColor = state.theme === 'light' ? 'rgba(33,37,41,0.3)' : 'rgba(255,255,255,0.3)';
        var neighborFontColor = state.theme === 'light' ? 'rgba(33,37,41,0.9)' : 'rgba(255,255,255,0.9)';
        
        var nodeUpdates = [];
        var allNodes = state.nodes.get();
        allNodes.forEach(function(node) {
            if (!state.originalNodeColors[node.id]) {
                state.originalNodeColors[node.id] = {
                    color: node.color,
                    opacity: node.opacity
                };
            }
            
            var targetState;
            if (selectedSet.has(node.id)) {
                targetState = 'selected';
            } else if (connectedNodeIds.has(node.id)) {
                targetState = 'connected';
            } else {
                targetState = 'dimmed';
            }
            newNodeState[node.id] = targetState;
            
            // Skip if state unchanged
            if (wasDimmed && prevNodeState[node.id] === targetState) return;
            
            if (targetState === 'selected') {
                nodeUpdates.push({
                    id: node.id,
                    opacity: 1.0,
                    font: { color: fontColor },
                    borderWidth: 3,
                    borderWidthSelected: 4
                });
            } else if (targetState === 'connected') {
                nodeUpdates.push({
                    id: node.id,
                    opacity: 0.85,
                    font: { color: neighborFontColor },
                    borderWidth: 2
                });
            } else {
                nodeUpdates.push({
                    id: node.id,
                    opacity: 0.2,
                    font: { color: dimmedFontColor }
                });
            }
        });
        if (nodeUpdates.length > 0) state.nodes.update(nodeUpdates);
        
        // Update edges: highlight connected edges, dim others
        var edgeUpdates = [];
        allEdges.forEach(function(edge) {
            var isConnected = connectedEdgeIds.has(edge.id);
            var targetState = isConnected ? 'connected' : 'dimmed';
            newEdgeState[edge.id] = targetState;
            
            // Skip if state unchanged
            if (wasDimmed && prevEdgeState[edge.id] === targetState) return;
            
            var original = state.originalEdgeColors[edge.id];
            var baseColor = (original && original.color) ? original.color : '#7f8c8d';
            var originalWidth = (original && original.width) ? original.width : 2;
            
            if (isConnected) {
                edgeUpdates.push({
                    id: edge.id,
                    color: { 
                        color: baseColor, 
                        highlight: baseColor,
                        hover: baseColor,
                        opacity: 1.0, 
                        inherit: false 
                    },
                    width: originalWidth + 1
                });
            } else {
                edgeUpdates.push({
                    id: edge.id,
                    color: { 
                        color: baseColor, 
                        highlight: baseColor,
                        hover: baseColor,
                        opacity: 0.1, 
                        inherit: false 
                    },
                    width: originalWidth
                });
            }
        });
        if (edgeUpdates.length > 0) state.edges.update(edgeUpdates);
        
        // Store dimmed state for delta detection
        state._dimmedNodeState = newNodeState;
        state._dimmedEdgeState = newEdgeState;
        state.selectedEdges = Array.from(connectedEdgeIds);
        
        console.log('[GraphExplorer] Dimmed graph - selected:', selectedNodeIds.length, 'nodes, updated:', nodeUpdates.length, 'nodes,', edgeUpdates.length, 'edges');
    }
    
    /**
     * Dim for ALIAS SELECTION mode: highlight ONLY selected nodes and edges
     * No inference of connected edges or neighbor nodes
     */
    function dimForAliasSelection(selectedNodeIds, selectedEdgeIds) {
        if (!state.nodes || !state.edges) return;
        
        var allNodesList = state.nodes.get();
        var allEdgesList = state.edges.get();
        var selectedNodeSet = new Set(selectedNodeIds);
        var selectedEdgeSet = new Set(selectedEdgeIds);
        
        var fontColor = state.theme === 'light' ? '#212529' : '#ffffff';
        var dimmedFontColor = state.theme === 'light' ? 'rgba(33,37,41,0.2)' : 'rgba(255,255,255,0.2)';
        
        // Store originals and update nodes
        var nodeUpdates = [];
        allNodesList.forEach(function(node) {
            if (!state.originalNodeColors[node.id]) {
                state.originalNodeColors[node.id] = {
                    color: node.color,
                    opacity: node.opacity
                };
            }
            
            if (selectedNodeSet.has(node.id)) {
                // Highlight selected nodes - preserve original color
                nodeUpdates.push({
                    id: node.id,
                    opacity: 1.0,
                    font: { color: fontColor },
                    borderWidth: 3,
                    borderWidthSelected: 4
                });
            } else {
                // Dim all non-selected nodes - preserve original color
                nodeUpdates.push({
                    id: node.id,
                    opacity: 0.15,
                    font: { color: dimmedFontColor }
                });
            }
        });
        state.nodes.update(nodeUpdates);
        
        // Update edges: only highlight explicitly selected edges
        var edgeUpdates = [];
        allEdgesList.forEach(function(edge) {
            var isSelected = selectedEdgeSet.has(edge.id);
            var original = state.originalEdgeColors[edge.id];
            var baseColor = (original && original.color) ? original.color : '#7f8c8d';
            var originalWidth = (original && original.width) ? original.width : 2;
            
            if (isSelected) {
                // Highlight selected edges with original color
                edgeUpdates.push({
                    id: edge.id,
                    color: { 
                        color: baseColor, 
                        highlight: baseColor,
                        hover: baseColor,
                        opacity: 1.0, 
                        inherit: false 
                    },
                    width: originalWidth + 1
                });
            } else {
                // Dim all non-selected edges
                edgeUpdates.push({
                    id: edge.id,
                    color: { 
                        color: baseColor, 
                        highlight: baseColor,
                        hover: baseColor,
                        opacity: 0.08, 
                        inherit: false 
                    },
                    width: originalWidth
                });
            }
        });
        state.edges.update(edgeUpdates);
        
        console.log('[GraphExplorer] Alias selection - nodes:', selectedNodeIds.length, ', edges:', selectedEdgeIds.length);
    }
    
    /**
     * Highlight multiple nodes for multi-selection (Ctrl+Click)
     */
    function highlightMultipleNodes(selectedNodeIds) {
        if (!state.nodes || !state.edges) return;
        if (!selectedNodeIds || selectedNodeIds.length === 0) {
            restoreAllNodes();
            if (state.network) {
                try { state.network.unselectAll(); } catch(e) {}
            }
            return;
        }
        
        dimNonSelectedNodes(selectedNodeIds);
        state.selectedNodes = selectedNodeIds;
        // Clear selectedAlias on manual selection (user clicked nodes directly)
        state.selectedAlias = null;
        
        // Sync vis.js selection so clicking empty canvas triggers deselectNode
        if (state.network) {
            try { state.network.selectNodes(selectedNodeIds); } catch(e) {}
        }
        
        console.log('[GraphExplorer] Multi-select highlight:', selectedNodeIds.length, 'nodes');
    }
    
    /**
     * Clear any highlighting and restore normal view
     */
    function clearHighlight() {
        restoreAllNodes();
    }
    
    /**
     * Restore all nodes and edges to original state - preserves custom colors.
     * Adaptive shadow: only enables shadows for small graphs.
     */
    function restoreAllNodes() {
        if (!state.nodes || !state.edges) return;
        
        var fontColor = state.theme === 'light' ? '#212529' : '#ffffff';
        
        // Adaptive shadow based on node count
        var nodeCount = state.nodes.length || 0;
        var shadowEnabled = nodeCount < 500;
        
        var nodeUpdates = [];
        var allNodes = state.nodes.get();
        allNodes.forEach(function(node) {
            var original = state.originalNodeColors[node.id] || {};
            var restoreColor = original.color || node.color;
            
            // If we have a nodeColorMap from updateNodeColors, use that color
            if (state.nodeColorMap && state.nodeColorMap[node.id]) {
                restoreColor = state.nodeColorMap[node.id];
            }
            
            nodeUpdates.push({
                id: node.id,
                color: restoreColor,
                opacity: 1.0,
                font: { color: fontColor },
                borderWidth: 2,
                borderWidthSelected: 2,
                shadow: shadowEnabled
            });
        });
        state.nodes.update(nodeUpdates);
        
        var edgeUpdates = [];
        var allEdges = state.edges.get();
        allEdges.forEach(function(edge) {
            var original = state.originalEdgeColors[edge.id];
            if (original) {
                var baseColor = original.color || '#7f8c8d';
                edgeUpdates.push({
                    id: edge.id,
                    color: { 
                        color: baseColor, 
                        highlight: baseColor,
                        hover: baseColor,
                        opacity: 1.0, 
                        inherit: false 
                    },
                    width: original.width,
                    dashes: original.dashes
                });
            } else {
                edgeUpdates.push({
                    id: edge.id,
                    color: { opacity: 1.0 },
                    width: 2
                });
            }
        });
        state.edges.update(edgeUpdates);
        
        // Clear stored node colors (edge colors are kept from initialization)
        state.originalNodeColors = {};
        state.selectedNodes = [];
        state.selectedEdges = [];
        state._dimmedNodeState = {};
        state._dimmedEdgeState = {};
        
        console.log('[GraphExplorer] Restored all nodes and edges to original state');
    }
    
    // ============================================
    // Message Listener for External Selection
    // ============================================
    function setupMessageListener() {
        window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'select_nodes') {
                var nodeIds = event.data.nodeIds || [];
                var edgeIds = event.data.edgeIds || null;
                
                if (nodeIds.length > 0) {
                    var existingNodeIds = nodeIds.filter(function(id) {
                        return state.nodes && state.nodes.get(id) !== null;
                    });
                    
                    if (existingNodeIds.length > 0) {
                        restoreAllNodes();
                        
                        var edgesToSelect = [];
                        if (edgeIds !== null && state.edges) {
                            edgesToSelect = edgeIds.filter(function(id) {
                                return state.edges.get(id) !== null;
                            });
                        }
                        
                        state.network.setSelection({
                            nodes: existingNodeIds,
                            edges: edgesToSelect
                        }, {
                            unselectAll: true,
                            highlightEdges: false
                        });
                        
                        state.selectedNodes = existingNodeIds;
                        
                        if (edgeIds !== null) {
                            dimForAliasSelection(existingNodeIds, edgesToSelect);
                        } else {
                            dimNonSelectedNodes(existingNodeIds);
                        }
                        
                        // Fit view to selected nodes — defer to next
                        // frame so vis.js uses the correct canvas size
                        // when the chat panel is open.
                        _syncNetworkSize();
                        requestAnimationFrame(function() {
                            if (!state.network) return;
                            if (existingNodeIds.length > 1) {
                                state.network.fit({
                                    nodes: existingNodeIds,
                                    animation: { duration: 500, easingFunction: 'easeInOutQuad' }
                                });
                            } else {
                                state.network.focus(existingNodeIds[0], {
                                    scale: 1.2,
                                    animation: { duration: 500, easingFunction: 'easeInOutQuad' }
                                });
                            }
                        });
                        
                        window.parent.postMessage({
                            type: 'pyvis_nodes_selected',
                            nodeIds: existingNodeIds,
                            edgeIds: state.selectedEdges,
                            count: existingNodeIds.length,
                            mode: edgeIds !== null ? 'alias' : 'single'
                        }, '*');
                    }
                }
            } else if (event.data && event.data.type === 'clear_selection') {
                if (state.network) {
                    state.network.unselectAll();
                }
                restoreAllNodes();
            }
        });
        
        console.log('[GraphExplorer] Message listener setup complete');
    }

    // ============================================
    // Theme Management
    // ============================================
    function toggleTheme() {
        state.theme = state.theme === 'dark' ? 'light' : 'dark';
        const container = document.querySelector('.graph-explorer');
        
        if (state.theme === 'light') {
            container?.classList.add('light-theme');
        } else {
            container?.classList.remove('light-theme');
        }

        // Update vis.js node font colors for theme
        updateNodeFontColors(state.theme);

        announceChange(`Theme switched to ${state.theme} mode`);
        return state.theme;
    }

    function updateNodeFontColors(theme) {
        if (!state.nodes) return;
        
        const fontColor = theme === 'light' ? '#212529' : '#ffffff';
        const allNodes = state.nodes.get();
        const updates = [];
        
        allNodes.forEach(function(node) {
            const currentFont = node.font || {};
            updates.push({
                id: node.id,
                font: {
                    ...currentFont,
                    color: fontColor
                }
            });
        });
        
        if (updates.length > 0) {
            state.nodes.update(updates);
        }
        
        console.log('[GraphExplorer] Updated font colors for', theme, 'theme');

        // Also update cluster node labels
        if (typeof ExplorerClustering !== 'undefined' && ExplorerClustering.updateClusterTheme) {
            ExplorerClustering.updateClusterTheme();
        }
    }

    function getTheme() {
        return state.theme;
    }

    // ============================================
    // Label Management
    // ============================================
    function toggleNodeLabels(visible, options) {
        var nextVisible = (typeof visible === 'boolean') ? visible : !state.nodeLabelsVisible;
        var silent = !!(options && options.silent);
        state.nodeLabelsVisible = nextVisible;
        
        if (!state.nodes) {
            console.warn('[GraphExplorer] No nodes DataSet available');
            return state.nodeLabelsVisible;
        }
        
        const allNodes = state.nodes.get();
        const updates = [];
        
        allNodes.forEach(function(node) {
            if (state.nodeLabelsVisible) {
                // Show labels - restore from hiddenLabel if exists
                if (node.hiddenLabel !== undefined) {
                    updates.push({
                        id: node.id,
                        label: node.hiddenLabel,
                        hiddenLabel: undefined
                    });
                }
            } else {
                // Hide labels - store in hiddenLabel
                if (node.label !== undefined && node.hiddenLabel === undefined) {
                    updates.push({
                        id: node.id,
                        hiddenLabel: node.label,
                        label: ''
                    });
                }
            }
        });
        
        if (updates.length > 0) {
            state.nodes.update(updates);
        }
        
        console.log('[GraphExplorer] Node labels', state.nodeLabelsVisible ? 'shown' : 'hidden');
        if (!silent) {
            announceChange(`Node labels ${state.nodeLabelsVisible ? 'shown' : 'hidden'}`);
        }
        return state.nodeLabelsVisible;
    }

    function toggleEdgeLabels(visible, options) {
        var nextVisible = (typeof visible === 'boolean') ? visible : !state.edgeLabelsVisible;
        var silent = !!(options && options.silent);
        state.edgeLabelsVisible = nextVisible;
        
        if (!state.edges) {
            console.warn('[GraphExplorer] No edges DataSet available');
            return state.edgeLabelsVisible;
        }
        
        const allEdges = state.edges.get();
        const updates = [];
        
        allEdges.forEach(function(edge) {
            if (state.edgeLabelsVisible) {
                // Restore label - get from hiddenLabel or keep current
                if (edge.hiddenLabel !== undefined) {
                    updates.push({
                        id: edge.id,
                        label: edge.hiddenLabel,
                        hiddenLabel: undefined,
                        font: { ...edge.font, size: edge.hiddenFontSize || 12 }
                    });
                }
            } else {
                // Hide label - store original and set font size to 0
                if (edge.label !== undefined && edge.label !== '') {
                    const currentFontSize = edge.font?.size || 12;
                    updates.push({
                        id: edge.id,
                        hiddenLabel: edge.label,
                        hiddenFontSize: currentFontSize,
                        font: { ...edge.font, size: 0 }
                    });
                }
            }
        });
        
        if (updates.length > 0) {
            state.edges.update(updates);
        }
        
        console.log('[GraphExplorer] Edge labels', state.edgeLabelsVisible ? 'shown' : 'hidden');
        if (!silent) {
            announceChange(`Edge labels ${state.edgeLabelsVisible ? 'shown' : 'hidden'}`);
        }
        return state.edgeLabelsVisible;
    }

    // ============================================
    // Physics Management
    // ============================================
    
    // Helper: run a physics setOptions call with fit suppressed.
    // vis.js calls initPhysics() on every setOptions({physics:...}),
    // which emits an internal "fit" event we cannot control via
    // stabilization.fit.  Suppressing fit on the network wrapper
    // avoids unwanted viewport recentering.
    function _setPhysicsOptions(opts) {
        if (!state.network) return;
        state.network._suppressFit = true;
        state.network.setOptions({ physics: opts });
        state.network._suppressFit = false;
    }

    function togglePhysics() {
        state.physicsEnabled = !state.physicsEnabled;
        
        _setPhysicsOptions({ enabled: state.physicsEnabled, stabilization: { fit: false } });
        
        // Update toolbar button
        if (typeof ExplorerToolbar !== 'undefined' && ExplorerToolbar.setButtonActive) {
            ExplorerToolbar.setButtonActive('btn-physics', state.physicsEnabled);
        }

        console.log('[GraphExplorer] Physics', state.physicsEnabled ? 'enabled' : 'disabled');
        announceChange(`Physics ${state.physicsEnabled ? 'enabled' : 'disabled'}`);
        return state.physicsEnabled;
    }
    
    function enablePhysics() {
        state.physicsEnabled = true;
        _setPhysicsOptions({ 
            enabled: true,
            stabilization: { fit: false }
        });
        
        // Update toolbar button
        if (typeof ExplorerToolbar !== 'undefined' && ExplorerToolbar.setButtonActive) {
            ExplorerToolbar.setButtonActive('btn-physics', true);
        }
        
        console.log('[GraphExplorer] Physics enabled');
    }
    
    function disablePhysics() {
        state.physicsEnabled = false;
        
        _setPhysicsOptions({ enabled: false, stabilization: { fit: false } });
        
        // Update toolbar button
        if (typeof ExplorerToolbar !== 'undefined' && ExplorerToolbar.setButtonActive) {
            ExplorerToolbar.setButtonActive('btn-physics', false);
        }
        
        console.log('[GraphExplorer] Physics disabled');
        announceChange('Physics disabled');
    }

    function improveLayout(iterations = 100) {
        if (state.network) {
            _syncNetworkSize();
            // Enable physics temporarily for layout optimization with faster timestep
            _setPhysicsOptions({ 
                enabled: true,
                timestep: 0.35,
                stabilization: {
                    enabled: true,
                    iterations: iterations,
                    updateInterval: 100,
                    fit: false
                }
            });
            state.network.stabilize(iterations);
            
            // Disable physics after stabilization if it wasn't enabled before.
            state.network.once('stabilizationIterationsDone', function() {
                if (!state.physicsEnabled) {
                    _setPhysicsOptions({ enabled: false, stabilization: { fit: false } });
                }
                console.log('[GraphExplorer] Layout optimization complete');
            });
        }
        announceChange('Optimizing layout...');
    }

    function fitToScreen() {
        if (state.network) {
            _syncNetworkSize();
            // Defer fit to next frame so vis.js has processed the
            // updated canvas dimensions from _syncNetworkSize().
            requestAnimationFrame(() => {
                if (!state.network) return;
                state.network.fit({ animation: false });
                console.log('[GraphExplorer] Fit to screen');
            });
        }
        announceChange('Fit to screen');
    }

    // ============================================
    // Layout Management
    // ============================================

    /**
     * Map from client layout names to server-side layout parameter values.
     * Server accepts: auto, spring, kamada_kawai, spectral, circular, shell, random, none
     */
    var CLIENT_TO_SERVER_LAYOUT = {
        'default':          'auto',
        'circular':         'circular',
        'radial':           'shell',
        'spring':           'spring',
        'kamada_kawai':     'kamada_kawai',
        'spectral':         'spectral',
        // hierarchical-* layouts are computed client-side by vis.js
        'hierarchical-UD':  'none',
        'hierarchical-LR':  'none',
        'hierarchical-DU':  'none',
    };

    /** Layouts computed entirely on the server (require API re-fetch). */
    var SERVER_ONLY_LAYOUTS = ['spring', 'kamada_kawai', 'spectral'];

    /**
     * Node count threshold below which we skip server-side layout and
     * just use vis.js client-side force-directed physics instead.
     */
    var SERVER_LAYOUT_MIN_NODES = 500;

    /**
     * Sync the client layout choice to the server-side layout parameter
     * stored in ExplorerAPI so that buildGraphDataUrl() sends the correct
     * `layout=` query parameter on the next data fetch.
     *
     * Also builds a layout_config dict from the current vis.js physics
     * settings so the server's NetworkX layout uses comparable spacing.
     */
    function _syncLayoutToApi(layout) {
        if (typeof ExplorerAPI === 'undefined') return;

        var serverLayout = CLIENT_TO_SERVER_LAYOUT[layout] || 'auto';

        if (serverLayout === 'auto') {
            ExplorerAPI.setLayoutAlgorithm(null);
            ExplorerAPI.setAutoLayout(true);
        } else {
            ExplorerAPI.setLayoutAlgorithm(serverLayout);
            ExplorerAPI.setAutoLayout(false);
        }

        // Build layout_config from current physics settings
        ExplorerAPI.setLayoutConfig(_buildLayoutConfig(layout));
    }

    /**
     * Translate the current vis.js physics params into a config dict for
     * the server-side NetworkX layout engine.
     *
     * Mapping logic:
     *   scale  ← springLength × 6   (vis.js 200px ≈ NX 1200)
     *   k      ← nodeDistance / 50   (repulsion solver) or springLength / 80
     *   iterations ← 50 (default, may increase for large graphs)
     *   seed   ← 42 (deterministic)
     */
    function _buildLayoutConfig(layout) {
        var solver = state.currentSolver || 'forceAtlas2Based';
        var defaults = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.DEFAULT_PHYSICS_OPTIONS)
            ? ExplorerConstants.DEFAULT_PHYSICS_OPTIONS[solver] || {}
            : {};

        // Read live values from vis.js physics body if available,
        // otherwise fall back to our stored defaults.
        var physicsBody = null;
        if (state.network && state.network.physics && state.network.physics.options) {
            var liveOpts = state.network.physics.options;
            physicsBody = liveOpts[solver] || null;
        }
        var params = physicsBody || defaults;

        var springLength = params.springLength || 200;
        var nodeDistance = params.nodeDistance || 100;

        var cfg = {
            seed: 42
        };

        // Scale: proportional to springLength
        cfg.scale = Math.round(springLength * 6);

        // k (optimal distance): only meaningful for spring layout
        if (layout === 'spring' || layout === 'default') {
            if (solver === 'repulsion' || solver === 'hierarchicalRepulsion') {
                cfg.k = +(nodeDistance / 50).toFixed(3);
            } else {
                cfg.k = +(springLength / 80).toFixed(3);
            }
        }

        // iterations: higher for denser graphs (springLength < 150 ⇒ tighter)
        cfg.iterations = springLength < 150 ? 80 : 50;

        return cfg;
    }

    /**
     * Set the graph layout algorithm.
     * @param {string} layout - 'default', 'hierarchical-UD', 'hierarchical-LR',
     *   'hierarchical-DU', 'radial', 'circular', 'spring', 'kamada_kawai', 'spectral'
     */
    function setLayout(layout) {
        state.currentLayout = layout;

        // Sync client layout to server-side layout parameter so that
        // "Reload Data" asks the server for matching pre-computed positions.
        _syncLayoutToApi(layout);

        // Server-only layouts: tell the server to compute positions and re-fetch
        // BUT only when the graph is large enough to justify a round-trip.
        if (SERVER_ONLY_LAYOUTS.indexOf(layout) !== -1) {
            var currentNodeCount = state.nodes ? state.nodes.length : 0;

            if (currentNodeCount <= SERVER_LAYOUT_MIN_NODES) {
                // Small graph — use vis.js force-directed layout instead
                console.log('[GraphExplorer] Graph has', currentNodeCount,
                    'nodes (<=' + SERVER_LAYOUT_MIN_NODES + ') — using vis.js force-directed instead of server', layout);
                if (state.network) {
                    state.network.setOptions({
                        layout: { hierarchical: { enabled: false }, improvedLayout: true }
                    });
                    if (state.physicsEnabled) {
                        _setPhysicsOptions({ enabled: true, stabilization: { fit: false } });
                    }
                    improveLayout(150);
                }
                announceChange('Layout: ' + layout + ' (client)');
                return;
            }

            if (state.network) {
                // Disable hierarchical if it was on
                state.network.setOptions({ layout: { hierarchical: { enabled: false } } });
            }
            // Re-fetch graph data from server with the selected layout
            if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.refreshGraph) {
                ExplorerAPI.refreshGraph(true);
            }
            console.log('[GraphExplorer] Server-side layout requested:', layout);
            announceChange('Layout: ' + layout);
            return;
        }

        if (!state.network) return;

        if (layout === 'default') {
            // Force-directed (disable hierarchical)
            state.network.setOptions({
                layout: {
                    hierarchical: { enabled: false },
                    improvedLayout: true
                }
            });
            // Re-enable physics to recalculate positions
            if (state.physicsEnabled) {
                _setPhysicsOptions({ enabled: true, stabilization: { fit: false } });
            }
            improveLayout(150);
        } else if (layout.startsWith('hierarchical')) {
            const direction = layout.split('-')[1] || 'UD';
            const sortMethod = state.hierarchicalSortMethod || 'hubsize';

            if (sortMethod === 'resource_name') {
                // Manual positioning by resource type — vis.js hierarchical engine
                // doesn't reliably honour node-level overrides, so we place nodes
                // ourselves (same pattern as applyCircularLayout / applyRadialLayout).
                state.network.setOptions({ layout: { hierarchical: { enabled: false } } });
                applyResourceNameLayout(direction);
            } else {
                // Standard vis.js hierarchical layout
                clearNodeLevels();
                state.network.setOptions({
                    layout: {
                        hierarchical: {
                            enabled: true,
                            direction: direction,
                            levelSeparation: state.hierarchicalLevelSeparation || 150,
                            treeSpacing: 200,
                            blockShifting: true,
                            edgeMinimization: true,
                            parentCentralization: true,
                            sortMethod: sortMethod
                        }
                    }
                });
            }
        } else if (layout === 'radial') {
            // Radial layout: place nodes in concentric rings by degree centrality
            state.network.setOptions({
                layout: { hierarchical: { enabled: false } }
            });
            applyRadialLayout();
        } else if (layout === 'circular') {
            // Circular layout: place all nodes in a circle
            state.network.setOptions({
                layout: { hierarchical: { enabled: false } }
            });
            applyCircularLayout();
        }

        console.log('[GraphExplorer] Layout set to:', layout);
        announceChange(`Layout: ${layout}`);
    }

    function setHierarchicalSort(sortMethod) {
        state.hierarchicalSortMethod = sortMethod;
        if (state.network && state.currentLayout && state.currentLayout.startsWith('hierarchical')) {
            // Re-run setLayout so the correct path (manual vs vis.js) is chosen
            setLayout(state.currentLayout);
        }
    }

    /**
     * Remove custom level overrides from all nodes so vis.js auto-sort
     * methods (hubsize / directed-by-edge) can work unimpeded.
     */
    function clearNodeLevels() {
        if (!state.nodes) return;
        const updates = state.nodes.getIds().map(id => ({ id: id, level: undefined }));
        state.nodes.update(updates);
    }

    /**
     * Manually position nodes in horizontal rows grouped by resource_name type.
     * Each unique resource type occupies one row (for UD/DU) or column (for LR).
     * Nodes within a row are spread evenly. Physics is disabled after positioning.
     */
    function applyResourceNameLayout(direction) {
        if (!state.nodes || !state.network) return;

        const LEVEL_MAP = buildResourceNameLevelMap();
        const allNodes = state.nodes.get();
        const levelSep = state.hierarchicalLevelSeparation || 150;
        const nodeSep = 120;

        // Group node ids by their level
        const levelGroups = {};
        allNodes.forEach(node => {
            const lv = LEVEL_MAP[getResourceNameLabel(node).toLowerCase()] ?? 5;
            if (!levelGroups[lv]) levelGroups[lv] = [];
            levelGroups[lv].push(node.id);
        });

        const sortedLevels = Object.keys(levelGroups).map(Number).sort((a, b) => a - b);
        const updates = [];

        sortedLevels.forEach((lv, rowIdx) => {
            const ids = levelGroups[lv];
            const count = ids.length;
            ids.forEach((id, colIdx) => {
                const axisA = rowIdx * levelSep;                    // main axis (rows)
                const axisB = (colIdx - (count - 1) / 2) * nodeSep; // cross axis (cols)
                if (direction === 'LR') {
                    updates.push({ id, x: axisA, y: axisB, fixed: false });
                } else if (direction === 'DU') {
                    updates.push({ id, x: axisB, y: -axisA, fixed: false });
                } else {
                    // UD (default)
                    updates.push({ id, x: axisB, y: axisA, fixed: false });
                }
            });
        });

        state.nodes.update(updates);
        _setPhysicsOptions({ enabled: false, stabilization: { fit: false } });
        setTimeout(() => { _syncNetworkSize(); requestAnimationFrame(() => { if (state.network) state.network.fit({ animation: { duration: 400, easingFunction: 'easeInOutQuad' } }); }); }, 50);
        console.log('[GraphExplorer] Applied resource_name layout:', sortedLevels.length, 'levels,', allNodes.length, 'nodes');
    }

    /**
     * Build the resource_name → level number map, appending unknown names
     * alphabetically so they always get stable levels beyond the known ones.
     */
    function buildResourceNameLevelMap() {
        const LEVEL_MAP = {
            'taxonomy':              0,
            'taxonomy root':         0,
            'root':                  0,
            'domain':                1,
            'occupation group':      1,
            'sector':                1,
            'industry':              1,
            'subcategory':           2,
            'sub category':          2,
            'category':              2,
            'occupation':            2,
            'job family':            2,
            'specialized skill':     3,
            'skill':                 3,
            'competency':            3,
            'role':                  3,
            'job':                   3,
            'job title':             3,
            'certification':         4,
            'tool':                  4,
            'technology':            4,
            'language':              4,
        };
        if (!state.nodes) return LEVEL_MAP;
        const unknown = new Set();
        state.nodes.get().forEach(node => {
            const rn = getResourceNameLabel(node).toLowerCase();
            if (!(rn in LEVEL_MAP)) unknown.add(rn);
        });
        Array.from(unknown).sort().forEach((name, i) => { LEVEL_MAP[name] = 5 + i; });
        return LEVEL_MAP;
    }

    /**
     * Assign vis.js `level` to each node based on resource_name hierarchy.
     * (Kept for potential future use; actual layout now uses applyResourceNameLayout.)
     */
    function applyResourceNameLevels() {
        if (!state.nodes) return;
        const LEVEL_MAP = buildResourceNameLevelMap();
        const allNodes = state.nodes.get();
        const updates = allNodes.map(node => ({
            id: node.id,
            level: LEVEL_MAP[getResourceNameLabel(node).toLowerCase()] ?? 5
        }));
        state.nodes.update(updates);
        console.log('[GraphExplorer] Applied resource_name levels to', updates.length, 'nodes');
    }

    /**
     * Return the best display label for the node's resource type.
     * Falls back to mixin_name, then label, rather than showing "Unknown".
     */
    function getResourceNameLabel(node) {
        if (node.resource_name && node.resource_name.trim() !== '') return node.resource_name.trim();
        if (node.mixin_name && node.mixin_name.trim() !== '') return node.mixin_name.trim();
        if (node.type && node.type.trim() !== '') return node.type.trim();
        if (node.label && node.label.trim() !== '') return node.label.trim();
        return 'Unknown';
    }


    /**
     * Color edges by their label using the shared color palette.
     * Assigns a distinct palette color per unique edge label and updates the DataSet.
     */
    function setEdgeColorByLabel() {
        if (!state.edges) return;
        const allEdges = state.edges.get();
        const labelSet = new Set();
        allEdges.forEach(e => labelSet.add(e.label || e.name || 'unnamed'));
        const labels = Array.from(labelSet);
        const palette = ExplorerUtils.getColorPalette();
        const labelColorMap = {};
        labels.forEach((lbl, i) => { labelColorMap[lbl] = palette[i % palette.length]; });
        // Store on state so legend can read it
        state.edgeLabelColorMap = labelColorMap;
        const updates = allEdges.map(e => ({
            id: e.id,
            color: { color: labelColorMap[e.label || e.name || 'unnamed'], highlight: '#ffffff', hover: '#cccccc' }
        }));
        state.edges.update(updates);
        // Also refresh originalEdgeColors so highlight/restore uses new colors
        updates.forEach(u => {
            state.originalEdgeColors[u.id] = {
                color: u.color.color,
                width: (state.originalEdgeColors[u.id] || {}).width || 1.5
            };
        });
        console.log('[GraphExplorer] Edge colors by label:', labels.length, 'types');
    }

    function setLevelSeparation(value) {
        state.hierarchicalLevelSeparation = value;
        if (state.network && state.currentLayout && state.currentLayout.startsWith('hierarchical')) {
            state.network.setOptions({
                layout: {
                    hierarchical: { levelSeparation: value }
                }
            });
        }
    }

    /**
     * Apply a circular layout (nodes evenly spaced on a circle).
     */
    function applyCircularLayout() {
        if (!state.nodes || !state.network) return;
        const nodeIds = state.nodes.getIds();
        const count = nodeIds.length;
        if (count === 0) return;

        const radius = Math.max(200, count * 20);
        const updates = nodeIds.map((id, i) => ({
            id: id,
            x: radius * Math.cos(2 * Math.PI * i / count),
            y: radius * Math.sin(2 * Math.PI * i / count)
        }));
        state.nodes.update(updates);
        _setPhysicsOptions({ enabled: false, stabilization: { fit: false } });
        setTimeout(() => { _syncNetworkSize(); requestAnimationFrame(() => { if (state.network) state.network.fit({ animation: true }); }); }, 50);
    }

    /**
     * Apply a radial layout based on node degree (most connected at center).
     */
    function applyRadialLayout() {
        if (!state.nodes || !state.network) return;
        const nodeIds = state.nodes.getIds();
        if (nodeIds.length === 0) return;

        // Calculate degrees
        const edges = state.edges ? state.edges.get() : [];
        const degreeMap = {};
        nodeIds.forEach(id => degreeMap[id] = 0);
        edges.forEach(e => {
            if (degreeMap[e.from] !== undefined) degreeMap[e.from]++;
            if (degreeMap[e.to] !== undefined) degreeMap[e.to]++;
        });

        // Sort by degree descending
        const sorted = nodeIds.slice().sort((a, b) => (degreeMap[b] || 0) - (degreeMap[a] || 0));

        // Assign to concentric rings
        const ringSize = Math.max(1, Math.ceil(sorted.length / 5));
        const updates = [];
        sorted.forEach((id, i) => {
            const ring = Math.floor(i / Math.max(1, ringSize));
            const posInRing = i - ring * ringSize;
            const nodesInThisRing = Math.min(ringSize, sorted.length - ring * ringSize);
            const radius = ring === 0 ? 0 : ring * 180;
            const angle = nodesInThisRing > 1 ? (2 * Math.PI * posInRing / nodesInThisRing) : 0;
            updates.push({
                id: id,
                x: radius * Math.cos(angle),
                y: radius * Math.sin(angle)
            });
        });
        state.nodes.update(updates);
        _setPhysicsOptions({ enabled: false, stabilization: { fit: false } });
        setTimeout(() => { _syncNetworkSize(); requestAnimationFrame(() => { if (state.network) state.network.fit({ animation: true }); }); }, 50);
    }

    // ============================================
    // Physics Parameter Management
    // ============================================

    /**
     * Change the physics solver.
     * @param {string} solver - 'barnesHut', 'forceAtlas2Based', 'repulsion'
     */
    function setPhysicsSolver(solver) {
        state.currentSolver = solver;
        if (!state.network) return;

        const defaults = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.DEFAULT_PHYSICS_OPTIONS)
            ? ExplorerConstants.DEFAULT_PHYSICS_OPTIONS[solver] || {}
            : {};

        const options = {
            physics: {
                solver: solver,
                [solver]: { ...defaults }
            }
        };
        state.network.setOptions(options);
        console.log('[GraphExplorer] Physics solver set to:', solver);
        announceChange(`Solver: ${solver}`);
    }

    /**
     * Update a single physics parameter for the current solver.
     * @param {string} param - e.g. 'springLength', 'gravitationalConstant', 'damping'
     * @param {number} value
     */
    function setPhysicsParam(param, value) {
        if (!state.network) return;
        const solver = state.currentSolver || 'forceAtlas2Based';
        const solverUpdate = {};
        solverUpdate[param] = value;

        const options = { physics: {} };
        options.physics[solver] = solverUpdate;
        state.network.setOptions(options);
        console.log(`[GraphExplorer] Physics param ${solver}.${param} = ${value}`);
    }

    /**
     * Reset physics parameters to defaults for a given solver.
     * @param {string} solver
     */
    function resetPhysicsDefaults(solver) {
        state.currentSolver = solver;
        if (!state.network) return;

        const defaults = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.DEFAULT_PHYSICS_OPTIONS)
            ? ExplorerConstants.DEFAULT_PHYSICS_OPTIONS[solver] || {}
            : {};

        const options = {
            physics: {
                solver: solver,
                [solver]: { ...defaults }
            }
        };
        state.network.setOptions(options);
        console.log('[GraphExplorer] Physics reset to defaults for:', solver);
        announceChange('Physics reset to defaults');
    }

    // ============================================
    // Selection Mode
    // ============================================
    function setSelectionMode(mode) {
        state.selectionMode = mode;
        
        // Note: multiselect is always enabled to allow Ctrl+Click selection
        // The 'mode' parameter controls UI behavior, not vis.js multiselect
        // vis.js multiselect: true means Ctrl+Click adds to selection
        
        console.log('[GraphExplorer] Selection mode:', mode);
        announceChange(`Selection mode: ${mode}`);
        return mode;
    }

    function getSelectionMode() {
        return state.selectionMode;
    }

    // ============================================
    // Node Appearance
    // ============================================
    function setColorByAttribute(attribute) {
        if (!state.nodes) {
            console.warn('[GraphExplorer] No nodes DataSet available');
            return;
        }
        
        state.currentColorAttribute = attribute;
        const allNodesList = state.nodes.get();
        const valueSet = new Set();
        
        // Get unique values
        allNodesList.forEach(function(node) {
            const val = attribute === 'resource_name'
                ? getResourceNameLabel(node)
                : (node[attribute] || (node.resource_name ? node.resource_name : 'Unknown'));
            valueSet.add(String(val));
        });
        
        const values = Array.from(valueSet);
        const colorPalette = ExplorerUtils.getColorPalette();
        const attrColorMap = {};
        
        values.forEach(function(val, idx) {
            attrColorMap[val] = colorPalette[idx % colorPalette.length];
        });
        
        // Store legend color map (attribute value -> color) for the legend window
        state.legendColorMap = attrColorMap;
        
        // Update nodes — apply palette colour
        const updates = [];
        state.nodeColorMap = {};
        
        allNodesList.forEach(function(node) {
            const val = String(attribute === 'resource_name'
                ? getResourceNameLabel(node)
                : (node[attribute] || (node.resource_name ? node.resource_name : 'Unknown')));
            const color = attrColorMap[val];
            state.nodeColorMap[node.id] = color;

            updates.push({ id: node.id, color: color });
            
            // Update original colors for restore - use state.originalNodeColors
            if (state.originalNodeColors[node.id]) {
                state.originalNodeColors[node.id].color = color;
            } else {
                state.originalNodeColors[node.id] = { color: color, opacity: 1.0 };
            }
        });
        
        state.nodes.update(updates);
        console.log('[GraphExplorer] Colored by', attribute, '-', values.length, 'categories');
        announceChange(`Colored by ${attribute}: ${values.length} categories`);
    }

    function setSizeScale(scale) {
        if (!state.nodes) {
            console.warn('[GraphExplorer] No nodes DataSet available');
            return;
        }
        
        state.currentSizeScale = scale;
        const allNodesList = state.nodes.get();
        const updates = [];
        
        allNodesList.forEach(function(node) {
            if (!state.originalNodeSizes[node.id]) {
                state.originalNodeSizes[node.id] = node.size || 25;
            }
            updates.push({
                id: node.id,
                size: state.originalNodeSizes[node.id] * scale
            });
        });
        
        state.nodes.update(updates);
        console.log('[GraphExplorer] Node size scale:', scale);
        announceChange(`Node size scale: ${scale}x`);
    }

    // ============================================
    // Responsive Handling
    // ============================================
    function setupResponsive() {
        const checkWidth = () => {
            const container = document.querySelector('.graph-explorer');
            if (!container) return;
            
            const width = container.offsetWidth;
            
            container.classList.remove('compact-mode', 'minimal-mode');
            
            if (width < 400) {
                container.classList.add('minimal-mode');
            } else if (width < 600) {
                container.classList.add('compact-mode');
            }
        };

        checkWidth();
        window.addEventListener('resize', ExplorerUtils.debounce(checkWidth, 150));
    }

    // ============================================
    // Accessibility Announcements
    // ============================================
    function announceChange(message) {
        // For screen readers
        const announcer = document.getElementById('explorer-announcer');
        if (announcer) {
            announcer.textContent = message;
        }
        console.log(message);
    }

    // ============================================
    // State Getters
    // ============================================
    function getState() {
        return { ...state };
    }

    // ============================================
    // Network Size Sync (chat-panel-aware)
    // ============================================
    /**
     * Explicitly sync the vis.js internal canvas dimensions to the current
     * #mynetwork container size.  Must be called before any fit / focus /
     * moveTo operation whenever the container may have been resized by CSS
     * (e.g. chat panel opening/closing) without a matching window-resize event.
     *
     * After calling setSize() we also redraw() so vis.js recalculates its
     * internal viewport origin.  Without this, subsequent fit() / focus()
     * operations use stale centre coordinates and nodes appear off-centre
     * when the chat panel is open.
     */
    function _syncNetworkSize() {
        if (!state.network) return;
        const container = document.getElementById('mynetwork');
        if (container && container.offsetWidth > 0) {
            state.network.setSize(container.offsetWidth + 'px', container.offsetHeight + 'px');
            state.network.redraw();
        }
    }

    // ============================================
    // Focus Node (pan and zoom to node)
    // ============================================
    function focusNode(nodeId, options = {}) {
        if (!state.network) {
            console.warn('[GraphExplorer] Network not initialized');
            return;
        }
        
        _syncNetworkSize();
        const scale = options.scale || 1.2;
        const animation = options.animation !== false;
        
        // Defer focus to next frame so vis.js has processed the
        // updated canvas dimensions from _syncNetworkSize().
        requestAnimationFrame(() => {
            if (!state.network) return;
            state.network.focus(nodeId, {
                scale: scale,
                animation: animation ? {
                    duration: 300,
                    easingFunction: 'easeInOutQuad'
                } : false
            });
            console.log('[GraphExplorer] Focused on node:', nodeId);
        });
    }

    // ============================================
    // Selection Management
    // ============================================
    
    /**
     * Set selected nodes directly (for external callers like MerlinGraph)
     */
    function setSelectedNodes(nodeIds) {
        state.selectedNodes = nodeIds || [];
        state.selectedAlias = null;
    }
    
    /**
     * Set selected edges directly
     */
    function setSelectedEdges(edgeIds) {
        state.selectedEdges = edgeIds || [];
    }
    
    /**
     * Notify about selection changes - updates operator states
     */
    function notifySelectionChange() {
        const count = state.selectedNodes ? state.selectedNodes.length : 0;
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.updateOperatorStates) {
            ExplorerWindows.updateOperatorStates(count);
        }
    }
    
    function selectNode(nodeId, nodeData) {
        state.selectedNodes = state.selectedNodes || [];
        
        // Check if node is already selected (for deselection)
        var alreadySelected = state.selectedNodes.includes(nodeId);
        var isClusterNode = typeof ExplorerClustering !== 'undefined' && ExplorerClustering.isCluster && ExplorerClustering.isCluster(nodeId);
        
        if (state.selectionMode === 'single') {
            if (alreadySelected && !isClusterNode) {
                // Clicking same node again - deselect it
                state.selectedNodes = [];
                restoreAllNodes();
                
                // Sync vis.js so canvas state matches
                if (state.network) {
                    try { state.network.unselectAll(); } catch(e) {}
                }
                
                console.log('[GraphExplorer] Node deselected:', nodeId);
                announceChange(`Deselected node: ${nodeData?.label || nodeId}`);
                
                // Clear context in bottom bar
                if (typeof ExplorerBottomBar !== 'undefined' && ExplorerBottomBar.clearContext) {
                    ExplorerBottomBar.clearContext();
                }
                
                // Send deselect message
                window.parent.postMessage({
                    type: 'pyvis_node_deselected',
                    nodeId: nodeId,
                    event: 'deselectNode'
                }, '*');
                notifySelectionChange();
                return;
            } else {
                // Single mode: replace selection with new node
                state.selectedNodes = [nodeId];
            }
        } else {
            // Multi mode: toggle selection
            if (alreadySelected) {
                // Remove from selection
                state.selectedNodes = state.selectedNodes.filter(function(id) { return id !== nodeId; });
                
                if (state.selectedNodes.length === 0) {
                    restoreAllNodes();
                    
                    // Sync vis.js so canvas state matches
                    if (state.network) {
                        try { state.network.unselectAll(); } catch(e) {}
                    }
                    
                    console.log('[GraphExplorer] All nodes deselected');
                    
                    // Clear context in bottom bar
                    if (typeof ExplorerBottomBar !== 'undefined' && ExplorerBottomBar.clearContext) {
                        ExplorerBottomBar.clearContext();
                    }
                    
                    window.parent.postMessage({
                        type: 'pyvis_node_deselected',
                        event: 'deselectNode'
                    }, '*');
                    notifySelectionChange();
                    return;
                } else {
                    // Update highlighting for remaining selection
                    dimNonSelectedNodes(state.selectedNodes);
                    
                    // Sync vis.js selection
                    if (state.network) {
                        try { state.network.selectNodes(state.selectedNodes); } catch(e) {}
                    }
                    
                    console.log('[GraphExplorer] Node removed from selection:', nodeId);
                    notifySelectionChange();
                    return;
                }
            } else {
                // Add to selection
                state.selectedNodes.push(nodeId);
            }
        }
        
        // Apply visual highlighting with opacity
        dimNonSelectedNodes(state.selectedNodes);
        
        // Sync vis.js selection so clicking empty canvas triggers deselectNode
        if (state.network) {
            try { state.network.selectNodes(state.selectedNodes); } catch(e) {}
        }
        
        console.log('[GraphExplorer] Node selected:', nodeId, nodeData);
        announceChange(`Selected node: ${nodeData?.label || nodeId}`);
        
        // Notify bottom bar for context
        if (typeof ExplorerBottomBar !== 'undefined' && ExplorerBottomBar.setContext) {
            ExplorerBottomBar.setContext({ type: 'node', id: nodeId, data: nodeData });
        }
        
        // Notify operator states
        notifySelectionChange();
        
        // Send postMessage for iframe embedding (e.g., Dash)
        var message = {
            type: 'pyvis_node_selected',
            nodeId: nodeId,
            nodeData: sanitizeForPostMessage(nodeData),
            event: 'selectNode'
        };
        window.parent.postMessage(message, '*');
    }

    function selectEdge(edgeId, edgeData) {
        state.selectedNodes = [];
        state.selectedEdges = state.selectedEdges || [];
        state.selectedEdges = [edgeId];
        
        console.log('[GraphExplorer] Edge selected:', edgeId, edgeData);
        announceChange(`Selected edge: ${edgeData?.label || edgeId}`);
        
        // Notify bottom bar for context
        if (typeof ExplorerBottomBar !== 'undefined' && ExplorerBottomBar.setContext) {
            ExplorerBottomBar.setContext({ type: 'edge', id: edgeId, data: edgeData });
        }
        
        // Send postMessage for iframe embedding (e.g., Dash)
        var message = {
            type: 'pyvis_edge_selected',
            edgeId: edgeId,
            edgeData: sanitizeForPostMessage(edgeData),
            event: 'selectEdge'
        };
        window.parent.postMessage(message, '*');
    }

    function clearSelection() {
        state.selectedNodes = [];
        state.selectedEdges = [];
        
        // Restore all nodes to original appearance
        restoreAllNodes();
        
        // Sync vis.js so canvas state matches
        if (state.network) {
            try { state.network.unselectAll(); } catch(e) {}
        }
        
        console.log('[GraphExplorer] Selection cleared');
        
        // Update operator states to reflect no selection
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.updateOperatorStates) {
            ExplorerWindows.updateOperatorStates(0);
        }
        
        // Clear context in bottom bar
        if (typeof ExplorerBottomBar !== 'undefined' && ExplorerBottomBar.clearContext) {
            ExplorerBottomBar.clearContext();
        }
        
        // Send postMessage for iframe embedding
        var message = {
            type: 'pyvis_node_deselected',
            event: 'deselectNode'
        };
        window.parent.postMessage(message, '*');
    }

    function toggleNodeSelection(nodeId) {
        state.selectedNodes = state.selectedNodes || [];
        
        const index = state.selectedNodes.indexOf(nodeId);
        if (index > -1) {
            state.selectedNodes.splice(index, 1);
            announceChange(`Deselected node`);
        } else {
            state.selectedNodes.push(nodeId);
            announceChange(`Added node to selection`);
        }
        
        // Update visual highlighting
        if (state.selectedNodes.length > 0) {
            dimNonSelectedNodes(state.selectedNodes);
            if (state.network) {
                try { state.network.selectNodes(state.selectedNodes); } catch(e) {}
            }
        } else {
            restoreAllNodes();
            if (state.network) {
                try { state.network.unselectAll(); } catch(e) {}
            }
        }
        
        console.log('[GraphExplorer] Selection toggled:', state.selectedNodes);
    }

    function getSelectedNodes() {
        return state.selectedNodes || [];
    }

    function getSelectedEdges() {
        return state.selectedEdges || [];
    }
    
    // Helper function to sanitize data for postMessage (remove functions and circular refs)
    function sanitizeForPostMessage(obj) {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj === 'function') return undefined;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) {
            return obj.map(function(item) { return sanitizeForPostMessage(item); });
        }
        var clean = {};
        for (var key in obj) {
            if (obj.hasOwnProperty(key)) {
                var value = obj[key];
                if (typeof value !== 'function') {
                    try {
                        JSON.stringify(value);
                        clean[key] = value;
                    } catch (e) {
                        // Skip non-serializable values
                    }
                }
            }
        }
        return clean;
    }

    // ============================================
    // Public API
    // ============================================
    global.GraphExplorer = {
        init,
        state,  // Expose state for direct access
        getState,
        toggleTheme,
        getTheme,
        updateNodeFontColors,
        toggleNodeLabels,
        toggleEdgeLabels,
        togglePhysics,
        enablePhysics,
        disablePhysics,
        improveLayout,
        fitToScreen,
        // Layout & Physics
        setLayout,
        setHierarchicalSort,
        setLevelSeparation,
        setPhysicsSolver,
        setPhysicsParam,
        resetPhysicsDefaults,
        focusNode,
        setSelectionMode,
        getSelectionMode,
        setColorByAttribute,
        setEdgeColorByLabel,
        setSizeScale,
        selectNode,
        selectEdge,
        clearSelection,
        toggleNodeSelection,
        getSelectedNodes,
        getSelectedEdges,
        setSelectedNodes,
        setSelectedEdges,
        announceChange,
        // New selection highlighting functions
        dimNonSelectedNodes,
        dimForAliasSelection,
        restoreAllNodes,
        storeOriginalEdgeColors,
        highlightMultipleNodes,
        clearHighlight,
        // Empty state management
        checkEmptyState,
        showEmptyState,
        hideEmptyState,
        // Chat-panel-aware canvas sync (call before fit/focus/moveTo in other modules)
        syncNetworkSize: _syncNetworkSize,
        // Fit suppression — use around physics setOptions calls in external modules
        suppressFit: function() {
            if (state.network) state.network._suppressFit = true;
        },
        allowFit: function() {
            if (state.network) state.network._suppressFit = false;
        },
    };

    // Alias for standalone.js compatibility (expects ExplorerCore)
    global.ExplorerCore = global.GraphExplorer;

})(window);
